import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { formatApiErrorMessage } from '@/lib/api-error'
import { Link } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { LoadingButton } from '@/components/loading-button'
import { RebookShipmentSheet } from '@/components/rebook-shipment-sheet'
import { EditShipmentSheet } from '@/components/edit-shipment-sheet'
import {
  Download,
  Send,
  MessageSquare,
  FileText,
  LayoutList,
} from 'lucide-react'
import {
  useRetryShipmentBooking,
  useBookShipment,
  useCorrectPurchaseOrderStatus,
  useCorrectShipmentStatus,
} from '@/api/hooks'
import {
  usePurchaseOrder,
  usePurchaseOrderDeliveryTimeline,
  useSkipSpecCredit,
} from '@/features/purchase-orders/hooks'
import { useAuth } from '@/hooks/use-auth'
import { BackButton } from '@/components/back-button'
import { EntityActivityBadge } from '@/components/entity-activity-badge'
import { POOperationalHold } from '@/components/po-operational-hold'
import { ManualStatusControl } from '@/components/manual-status-control'
import { PO_STATUS_CHOICES } from '@/components/manual-status-options'
import { cn, parseUTCDate } from '@/lib/utils'
import { OrderStatusChips } from '@/components/order-status-chips'
import {
  usePOCommunications,
  usePOInvoiceCommunications,
  useApproveMessage,
  useRejectMessage,
  useSendMessage,
  useUpdateMessage,
  useSendPOEmails,
} from '@/features/communications/hooks'
import type { ShipmentDocOption } from '@/features/communications/types'
import { POConversations } from '@/features/communications/po-conversations'
import {
  RecordCommunicationInline,
  NewThreadDialog,
} from '@/features/communications/po-thread-actions'
import { InvoiceExpectationPanel } from '@/features/purchase-orders/invoice-expectation'
import { LineItemsCard } from '@/features/purchase-orders/line-items'
import { POCommentsCard } from '@/features/purchase-orders/comments'
import { POSettingsPanel } from '@/features/purchase-orders/settings'
import { POShippingSection } from '@/features/purchase-orders/shipping'
import { POTrackingNumbers } from '@/features/purchase-orders/tracking-numbers'
import { POActionsSection, PurchaseOrderActionCard } from '@/features/purchase-orders/actions'
import { PODetailsCard } from '@/features/purchase-orders/details'
import { useWarehousePurchaseOrder } from '@/api/warehouse-sourcing'
import { ProjectServiceTierPicker } from '@/components/service-tier'
import { asServiceTier } from '@/lib/service-tier'
import { DeliveryTimelineCard } from '@/components/delivery-timeline'
import { PurchaseOrderTimelineBar } from '@/features/projects/project-expected-timeline'
import { POSpecCreditCard } from '@/features/purchase-orders/spec-credit'
import { POPdfView } from '@/features/purchase-orders/pdf-view'
import { usePageTitle } from '@/lib/page-title'
import { useActionFocus, useMessageFocus } from '@/hooks/use-action-focus'
import { ActionEmailContext } from '@/features/communications/action-email'
import { WaitingOn } from '@/features/owed-items/waiting-on'
import { usePurchaseOrderWarrantyClaims } from '@/features/warranty-claims/api'
import { WarrantyClaimsSection } from '@/features/warranty-claims/warranty-claims-section'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

interface PurchaseOrderDetailPageProps {
  poId: string
  tab?: string
  threadUuid?: string
  actionUuid?: string
  /** An email to open and highlight: an action item card's link to its source email. */
  messageId?: number
}

export function PurchaseOrderDetailPage({
  poId,
  threadUuid,
  actionUuid,
  messageId,
}: PurchaseOrderDetailPageProps) {
  const { data: po, isLoading } = usePurchaseOrder(poId)
  const { data: warrantyClaims } = usePurchaseOrderWarrantyClaims(poId)
  const fromWarehouse = useWarehousePurchaseOrder(poId).data?.from_warehouse === true
  const deliveryTimeline = usePurchaseOrderDeliveryTimeline(poId)
  useActionFocus(actionUuid, !!po)
  usePageTitle(po && `PO ${po.po_number}`)
  const correctPOStatus = useCorrectPurchaseOrderStatus(poId)
  const correctShipmentStatus = useCorrectShipmentStatus(poId)
  const { data: threads, isLoading: threadsLoading } = usePOCommunications(poId)
  const { data: invoiceThreads } = usePOInvoiceCommunications(poId)
  useMessageFocus(messageId, !!po && !threadsLoading)
  const { getAccessToken } = useAuth()

  const [showResolvedActions, setShowResolvedActions] = useState(false)
  const [rebookShipmentId, setRebookShipmentId] = useState<number | null>(null)
  const [editShipmentId, setEditShipmentId] = useState<number | null>(null)
  const [showAddShipment, setShowAddShipment] = useState(false)
  /** Staff can swap the page for the PO's rendered PDF without downloading it. */
  const [view, setView] = useState<'page' | 'pdf'>('page')
  /** Cancelled shipments are hidden behind a toggle so the active booking
   * stays prominent and ops doesn't scroll past tombstoned attempts. */
  const [showCancelledShipments, setShowCancelledShipments] = useState(false)
  const approveMutation = useApproveMessage()
  const rejectMutation = useRejectMessage()
  const sendMutation = useSendMessage()
  const updateMutation = useUpdateMessage()
  const sendPOEmailsMutation = useSendPOEmails()
  const retryBookingMutation = useRetryShipmentBooking(poId)
  const bookShipmentMutation = useBookShipment(poId)
  const skipSpecCreditMutation = useSkipSpecCredit(poId)
  const handleSkipSpecCredit = () =>
    skipSpecCreditMutation.mutate(undefined, {
      onSuccess: () => toast.success('Spec credit skipped. PO queued to send.'),
      onError: (err) => toast.error(formatApiErrorMessage('PO not queued', err)),
    })

  const handleDownloadPdf = async () => {
    if (!po) return
    const token = await getAccessToken()
    if (!token) return
    const response = await fetch(`${API_BASE_URL}/dashboard/purchase-orders/${po.uuid}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) return
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = po.pdf_filename || `PO-${po.po_number}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Stored shipment documents (BOL/label PDFs) on this PO that can be
  // re-attached to a manual reply. We surface non-cancelled shipments only —
  // a cancelled shipment's label is for a voided booking and would confuse
  // the manufacturer if re-sent.
  const shipmentDocOptions: ShipmentDocOption[] = (po?.shipments ?? [])
    .filter((s) => s.status !== 'cancelled')
    .flatMap((s) => {
      const label = s.ship_from_name ? `Shipment to ${s.ship_from_name}` : `Shipment ${s.id}`
      const opts: ShipmentDocOption[] = []
      if (s.bol_pdf_filename) {
        opts.push({
          shipmentId: s.id,
          shipmentLabel: label,
          docType: 'bol',
          filename: s.bol_pdf_filename,
        })
      }
      if (s.label_pdf_filename) {
        opts.push({
          shipmentId: s.id,
          shipmentLabel: label,
          docType: 'label',
          filename: s.label_pdf_filename,
        })
      }
      return opts
    })

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!po) {
    return (
      <div className="mx-auto max-w-7xl">
        <p className="text-muted-foreground">Purchase order not found</p>
      </div>
    )
  }

  const openActions = po.actions?.filter((a) => a.status === 'open') || []
  const resolvedActions = po.actions?.filter((a) => a.status !== 'open') || []
  const canSkipSpecCredit = ['spec_credit_pending', 'spec_credit_sent', 'spec_credit_expired'].includes(po.status)
  // Clicking the Order pill opens the status correction (USA-1432).
  const poStatusControl = (trigger?: ReactNode) => (
    <ManualStatusControl
      entityLabel="purchase order"
      currentStatus={po.status}
      statusRevision={po.status_revision}
      statusSource={po.status_source}
      statuses={PO_STATUS_CHOICES}
      loading={correctPOStatus.isPending}
      onSubmit={(body, callbacks) => correctPOStatus.mutate(body, callbacks)}
      trigger={trigger}
      triggerLabel={trigger ? po.status_summary?.order?.label : undefined}
    />
  )

  return (
    <div className="mx-auto min-w-0 max-w-[1600px]">
      {/* Back arrow beside the project: the title below already names the PO. */}
      <nav className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
        <BackButton iconOnly fallback={{ to: '/purchase-orders' }} fallbackLabel="Back to Purchase Orders" />
        {po.internal_order?.project && (
          <Link
            to="/projects/$projectId"
            params={{ projectId: po.internal_order.project.uuid }}
            title={po.internal_order.project.name}
            className="min-w-0 truncate hover:text-foreground hover:underline"
          >
            {po.internal_order.project.name}
          </Link>
        )}
      </nav>

      {/* Below lg everything after the title flows as one wrapping row: chips, Hold, view, download. */}
      <div className="mb-5 mt-1 flex flex-wrap items-center gap-2 lg:flex-nowrap lg:justify-between lg:gap-3">
        <div className="max-lg:contents lg:min-w-0">
          <h1 className="break-words text-lg font-bold tracking-tight max-lg:w-full sm:text-xl md:text-2xl">
            Purchase Order #{po.po_number}
          </h1>
          <div className="flex flex-wrap items-center gap-2 max-lg:contents lg:mt-1">
            <OrderStatusChips
              className="max-lg:contents"
              order={po.status_summary?.order}
              shipping={po.status_summary?.shipping}
              ap={po.status_summary?.ap}
              wrapOrder={(pill) => poStatusControl(pill)}
            />
            {/* Without an Order pill, the PO's own status is the pill to click. */}
            {!po.status_summary?.order && poStatusControl()}
            {po.internal_order?.project && (
              <ProjectServiceTierPicker
                projectUuid={po.internal_order.project.uuid}
                tier={asServiceTier(po.project_service_tier)}
              />
            )}
            <POOperationalHold po={po} />
            <EntityActivityBadge entityType="purchase_order" entityId={po.id} />
          </div>
        </div>
        <div className="max-lg:contents lg:flex lg:flex-col lg:items-end lg:gap-2">
          <div className="max-lg:contents lg:flex lg:items-center lg:justify-end lg:gap-2">
            {po.pdf_filename && (
              <div
                role="group"
                aria-label="Purchase order view"
                className="inline-grid grid-cols-2 rounded-lg border bg-muted/40 p-1"
              >
                {(
                  [
                    { value: 'page', label: 'Details', Icon: LayoutList },
                    { value: 'pdf', label: 'PO PDF', Icon: FileText },
                  ] as const
                ).map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={view === value}
                    onClick={() => setView(value)}
                    className={cn(
                      'inline-flex min-h-8 cursor-pointer items-center justify-center gap-2 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                      view === value && 'bg-background text-foreground shadow-sm',
                    )}
                  >
                    <Icon className="size-3.5" aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
            )}
            {po.pdf_filename && (
              <Button
                onClick={handleDownloadPdf}
                className="cursor-pointer max-sm:w-9 max-sm:px-0"
                aria-label="Download PO"
                title="Download PO"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="max-sm:hidden">Download PO</span>
              </Button>
            )}
          </div>
          <div className="max-lg:w-full">
            <POTrackingNumbers shipments={po.shipments ?? []} />
          </div>
        </div>
      </div>

      {po.operational_held_at && (
        <div className="mb-5 max-w-3xl text-sm">
          <p className="font-medium">{po.operational_hold_reason}</p>
          {po.operational_hold_note && (
            <p className="mt-0.5 whitespace-pre-wrap text-muted-foreground">
              {po.operational_hold_note}
            </p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            Held {parseUTCDate(po.operational_held_at).toLocaleString()}
            {po.operational_held_by ? ` by ${po.operational_held_by}` : ''}
          </p>
        </div>
      )}

      {view === 'pdf' && po.pdf_filename ? (
        <POPdfView poUuid={po.uuid} poNumber={po.po_number} />
      ) : (
        /* Two-column Shopify-style layout. Below lg both columns dissolve (`contents`) and
           `order` interleaves them: work first, then the reference cards. */
        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          {/* LEFT COLUMN — Main content */}
          <div className="min-w-0 max-lg:contents lg:space-y-6">
            {/* Line Items */}
            <div className="min-w-0 max-lg:order-1">
              <LineItemsCard po={po} />
            </div>

            <div className="min-w-0 max-lg:order-2">
              <POActionsSection
                canSkipSpecCredit={canSkipSpecCredit}
                handleSkipSpecCredit={handleSkipSpecCredit}
                skippingSpecCredit={skipSpecCreditMutation.isPending}
                po={po}
                poId={poId}
                showResolvedActions={showResolvedActions}
                setShowResolvedActions={setShowResolvedActions}
                openActions={openActions}
                resolvedActions={resolvedActions}
              />
            </div>

            <div className="min-w-0 space-y-6 max-lg:order-4">
              <POShippingSection
                po={po}
                showCancelledShipments={showCancelledShipments}
                setShowCancelledShipments={setShowCancelledShipments}
                showAddShipment={showAddShipment}
                setShowAddShipment={setShowAddShipment}
                setEditShipmentId={setEditShipmentId}
                setRebookShipmentId={setRebookShipmentId}
                correctShipmentStatus={correctShipmentStatus}
                bookShipmentMutation={bookShipmentMutation}
                retryBookingMutation={retryBookingMutation}
                getAccessToken={getAccessToken}
              />
            </div>

            {/* Only a PO whose item a customer filed a warranty claim for shows it (USA-915). */}
            {warrantyClaims?.length ? (
              <div className="min-w-0 max-lg:order-4">
                <WarrantyClaimsSection claims={warrantyClaims} onPurchaseOrder focusActionUuid={actionUuid} />
              </div>
            ) : null}

            {/* Communications: nobody to email about a PO pulled from our own warehouse (USA-1574). */}
            <div
              className="min-w-0 space-y-4 max-lg:order-5"
              hidden={fromWarehouse && !threadsLoading && !threads?.length && !invoiceThreads?.length}
            >
              <ActionEmailContext.Provider
                value={{
                  focusMessageId: messageId,
                  renderAction: (item) => {
                    const action = po.actions?.find((candidate) => candidate.id === item.id)
                    return action ? <PurchaseOrderActionCard po={po} action={action} /> : null
                  },
                }}
              >
                <POConversations
                  hideResolved
                  initialThreadUuid={threadUuid}
                  threads={threads && [...threads, ...(invoiceThreads ?? [])]}
                  invoiceForThread={(thread) => {
                    const invoice = po.ap_invoice_links?.find(
                      (link) => link.invoice_id === thread.ap_invoice_id,
                    )?.invoice
                    return invoice
                      ? { uuid: invoice.uuid, number: invoice.invoice_number || String(invoice.id) }
                      : undefined
                  }}
                  isLoading={threadsLoading}
                  actions={po.actions ?? []}
                  poUuid={poId}
                  hasPoPdf={!!po.pdf_filename}
                  hasQuotePdf={!!po.spec_credit_request?.quote_pdf_filename}
                  shipmentDocOptions={shipmentDocOptions}
                  onApprove={(uuid) => approveMutation.mutate(uuid)}
                  onReject={(uuid) => rejectMutation.mutate(uuid)}
                  onSend={(uuid) => sendMutation.mutate(uuid)}
                  onUpdate={(uuid, body) =>
                    updateMutation.mutate(
                      { messageUuid: uuid, body },
                      { onError: (err) => toast.error(formatApiErrorMessage('Draft not saved', err)) },
                    )
                  }
                  newThreadAction={
                    fromWarehouse ? undefined : <NewThreadDialog
                      poUuid={poId}
                      hasPoPdf={!!po.pdf_filename}
                      hasQuotePdf={!!po.spec_credit_request?.quote_pdf_filename}
                    />
                  }
                  emptyState={
                    <Card>
                      <CardContent className="flex flex-col items-center gap-3 py-8">
                        <MessageSquare className="h-8 w-8 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">No communications yet</p>
                        <div className="flex gap-2">
                          {po.status === 'generated' && po.pdf_filename && (
                            <LoadingButton
                              size="sm"
                              loading={sendPOEmailsMutation.isPending}
                              loadingText="Sending..."
                              onClick={() => sendPOEmailsMutation.mutate([po.uuid])}
                            >
                              <Send className="mr-1 h-3 w-3" />
                              Send PO Email
                            </LoadingButton>
                          )}
                          <NewThreadDialog
                            poUuid={poId}
                            hasPoPdf={!!po.pdf_filename}
                            hasQuotePdf={!!po.spec_credit_request?.quote_pdf_filename}
                          />
                          <RecordCommunicationInline poUuid={poId} poId={po.id} />
                        </div>
                      </CardContent>
                    </Card>
                  }
                />
              </ActionEmailContext.Provider>
            </div>

          </div>

          {/* RIGHT SIDEBAR */}
          <aside
            aria-label="Purchase order details and instructions"
            className="min-w-0 max-lg:contents lg:space-y-5"
          >
            {deliveryTimeline.data ? (
              <div className="min-w-0 max-lg:order-3">
                <DeliveryTimelineCard
                  timeline={deliveryTimeline.data}
                  chart={<PurchaseOrderTimelineBar timeline={deliveryTimeline.data} />}
                />
              </div>
            ) : null}
            <div className="min-w-0 max-lg:order-6">
              <PODetailsCard
                po={po}
                shipToName={
                  po.ship_to_name && po.internal_order?.customer_group ? (
                    <Link
                      to="/customers/$groupId"
                      params={{ groupId: po.internal_order.customer_group.uuid }}
                      className="text-primary hover:underline"
                    >
                      {po.ship_to_name}
                    </Link>
                  ) : undefined
                }
              />
            </div>

            <WaitingOn scope={{ kind: 'purchase_order', poUuid: po.uuid }} />

            {po.internal_order?.project && (
              <POCommentsCard
                projectUuid={po.internal_order.project.uuid}
                poUuid={po.uuid}
                poNumber={po.po_number}
                className="min-w-0 max-lg:order-9"
              />
            )}

            {po.spec_credit_request && (
              <div className="min-w-0 max-lg:order-7">
                <POSpecCreditCard
                  po={po}
                  getAccessToken={getAccessToken}
                  canSkipSpecCredit={canSkipSpecCredit}
                  handleSkipSpecCredit={handleSkipSpecCredit}
                  skippingSpecCredit={skipSpecCreditMutation.isPending}
                />
              </div>
            )}

            {po.invoice_expectation && (
              <div className="min-w-0 max-lg:order-8">
                <InvoiceExpectationPanel
                  poUuid={po.uuid}
                  expectation={po.invoice_expectation}
                  invoiceLinks={po.ap_invoice_links}
                />
              </div>
            )}

            <div className="min-w-0 max-lg:order-10">
              <POSettingsPanel
                poUuid={po.uuid}
                automationDisabled={po.automation_disabled}
                projectTrackingEnabled={po.internal_order?.project?.tracking_enabled ?? true}
              />
            </div>
          </aside>
        </div>
      )}

      {(() => {
        const rebookShipment = po.shipments?.find((s) => s.id === rebookShipmentId)
        if (!rebookShipment) return null
        return (
          <RebookShipmentSheet
            key={rebookShipment.id}
            open={rebookShipmentId !== null}
            onOpenChange={(open) => {
              if (!open) setRebookShipmentId(null)
            }}
            poUuid={po.uuid}
            shipment={rebookShipment}
          />
        )
      })()}

      {(() => {
        const editShipment = po.shipments?.find((s) => s.id === editShipmentId)
        if (!editShipment) return null
        return (
          <EditShipmentSheet
            key={editShipment.id}
            open={editShipmentId !== null}
            onOpenChange={(open) => {
              if (!open) setEditShipmentId(null)
            }}
            poUuid={po.uuid}
            shipment={editShipment}
            policyOnly
          />
        )
      })()}

    </div>
  )
}
